import { FLAGS, POSITION_SORT_WEIGHT, TEAM_ODDS } from '../data/constants.js';
import { gt, globalLocale } from '../i18n/index.js';

export function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function escapeAttribute(value) {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '-');
}

export function flag(teamId) {
  if (teamId === 'tbd') return '<span class="shield">⬟</span>';
  return FLAGS[teamId] || '🏳️';
}

export function ratingStars(value) {
  const rating = Number(value || 0);
  let stars = 1;
  if (rating >= 190) stars = 6;
  else if (rating >= 180) stars = 5.5;
  else if (rating >= 170) stars = 5;
  else if (rating >= 160) stars = 4.5;
  else if (rating >= 150) stars = 4;
  else if (rating >= 140) stars = 3.5;
  else if (rating >= 130) stars = 3;
  else if (rating >= 120) stars = 2.5;
  else if (rating >= 110) stars = 2;
  else if (rating >= 100) stars = 1.5;
  const full = Math.floor(stars);
  const half = stars % 1 > 0;
  return '★'.repeat(full) + (half ? '☆' : '');
}

export function playerInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'P';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

export function shortDate(value) {
  if (!value) return 'TBD';
  const [date, time] = value.split(' ');
  const [, month, day] = date.split('-');
  return `${month}/${day}${time ? ` ${time}` : ''}`;
}

export function newRunSeed() {
  const cryptoValues = new Uint32Array(2);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(cryptoValues);
  else {
    cryptoValues[0] = Math.floor(Math.random() * 0xffffffff);
    cryptoValues[1] = Date.now() & 0xffffffff;
  }
  return Number((BigInt(cryptoValues[0]) << 32n) ^ BigInt(cryptoValues[1]));
}

export function deterministicNoise(value) {
  let hash = 0;
  for (const char of value) hash = (hash * 31 + char.charCodeAt(0)) % 17;
  return hash;
}

export function stableIndex(seed, length) {
  const text = String(seed || '');
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % Math.max(1, length);
}

export function pickTemplate(templates, seed, values = {}) {
  const template = templates[stableIndex(seed, templates.length)];
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), template);
}

export function positionWeight(position) {
  return POSITION_SORT_WEIGHT[position] ?? 4;
}

export function translatePosition(position) {
  const locale = globalLocale?.value;
  const isZh = locale?.startsWith('zh');
  const map = {
    GK: isZh ? '门将' : 'GK',
    CB: isZh ? '中后卫' : 'CB',
    RB: isZh ? '右后卫' : 'RB',
    LB: isZh ? '左后卫' : 'LB',
    RWB: isZh ? '右翼卫' : 'RWB',
    LWB: isZh ? '左翼卫' : 'LWB',
    DM: isZh ? '防守中场' : 'DM',
    CM: isZh ? '中前卫' : 'CM',
    AM: isZh ? '进攻中场' : 'AM',
    RW: isZh ? '右边锋' : 'RW',
    LW: isZh ? '左边锋' : 'LW',
    ST: isZh ? '前锋' : 'ST',
    CF: isZh ? '中锋' : 'CF',
  };
  return map[position] || position || '';
}

export function playerLocalizedName(player) {
  if (!player) return '';
  const names = player.names;
  if (names && typeof names === 'object') {
    const locale = globalLocale?.value;
    if (locale) {
      if (names[locale]) return names[locale];
      const prefix = locale.split('-')[0];
      if (prefix !== locale && names[prefix]) return names[prefix];
    }
    if (names.en) return names.en;
  }
  return player.name || '';
}

export function jerseyNumber(index) {
  return index + 1;
}

export function emptyPlayerStats() {
  return { apps: 0, goals: 0, assists: 0, redCards: 0, ratedApps: 0, averageRating: 0 };
}

export function playerStatus(stats) {
  if (stats?.redCards > 0) return { type: 'red', label: gt('team.status.redCard') };
  return null;
}

export function nextMatchPlayerStatus(playerId, teamId, stats, run) {
  const next = run?.nextMatch;
  const isNextTeam = next && (next.homeTeamId === teamId || next.awayTeamId === teamId);
  if (!isNextTeam) return playerStatus(stats);
  if (stats?.redCards > 0) return { type: 'red-card', label: gt('team.status.redCard') };
  return null;
}

export function tacticSnapshotSummary(tactic) {
  if (!tactic) return gt('tactics.noData');
  const mentality = tactic.base?.mentality || '-';
  const focus = tactic.inPossession?.attackFocus || '-';
  const pressing = tactic.outOfPossession?.pressingIntensity ?? '-';
  const line = tactic.outOfPossession?.defensiveLine ?? '-';
  return gt('helpers.tacticSummary', { mentality, focus, pressing, line });
}

export function percentage(value) {
  return `${Math.round(Number(value || 0))}%`;
}

export function rankLabel(index, rows, key) {
  if (index === 0) return '1';
  const previous = rows[index - 1];
  const current = rows[index];
  return previous && current && previous[key] === current[key] ? '=' : String(index + 1);
}

export function passRate(stats) {
  return stats?.passesAttempted ? `${Math.round((stats.passesCompleted / stats.passesAttempted) * 100)}%` : '-';
}

export function passRateValue(stats) {
  return stats?.passesAttempted ? Math.round((stats.passesCompleted / stats.passesAttempted) * 100) : 0;
}

export function playerPassRate(player) {
  return player?.passesAttempted ? `${Math.round((player.passesCompleted / player.passesAttempted) * 100)}%` : '-';
}

export function metric(label, value) {
  return `<div class="metric"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
}

export function stageName(stage) {
  const translated = gt('stage.' + stage);
  return translated || stage;
}

export function goalStateLabel(teamGoals, opponentGoals) {
  if (teamGoals === opponentGoals) return gt('helpers.goalState.equalize');
  if (teamGoals === 1 && opponentGoals === 0) return gt('helpers.goalState.lead');
  if (teamGoals > opponentGoals) return gt('helpers.goalState.extend');
  return gt('helpers.goalState.chase');
}

export function chanceStateLabel(teamGoals, opponentGoals) {
  if (teamGoals > opponentGoals) return gt('helpers.chanceState.extend');
  if (teamGoals === opponentGoals) return gt('helpers.chanceState.break');
  if (opponentGoals - teamGoals >= 3) return gt('helpers.chanceState.face');
  return gt('helpers.chanceState.close');
}

export function minuteText(event) {
  const minute = `${event.minute || 0}`;
  const stoppage = event.stoppageTime ? gt('helpers.stoppageTime', { minute: event.stoppageTime }) : '';
  return gt('helpers.minute', { minute, stoppage });
}

export function playerNameById(match, playerId) {
  if (!playerId) return '';
  const e = (match.playerRatings || []).find((p) => p.playerId === playerId);
  if (!e) return '';
  const names = e.names;
  if (names && typeof names === 'object') {
    const locale = globalLocale?.value;
    if (locale) {
      if (names[locale]) return names[locale];
      const prefix = locale.split('-')[0];
      if (prefix !== locale && names[prefix]) return names[prefix];
    }
    if (names.en) return names.en;
  }
  return e.name;
}

export function stageKey(stage) {
  return String(stage || '').toLowerCase().replaceAll(' ', '-');
}

export function formatFormText(value) {
  const text = String(value || '-');
  return text.replaceAll('W', gt('helpers.formText.W')).replaceAll('D', gt('helpers.formText.D')).replaceAll('L', gt('helpers.formText.L'));
}

export function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function findTeamById(teams, teamId) {
  return teams?.find((team) => team.id === teamId);
}

export function findTeamName(teams, teamId) {
  return findTeamById(teams, teamId)?.name || teamId;
}

export function translatedTeamName(teamId) {
  if (teamId === 'tbd') return gt('knockout.tbd');
  const name = gt('teamNames.' + teamId);
  return name || teamId;
}

export function teamStrength(teams, teamId) {
  return Number(findTeamById(teams, teamId)?.strength || 150);
}

export function teamOdds(teamId) {
  return TEAM_ODDS[teamId] || 2000;
}

export function oddsRank(teamId) {
  const sorted = Object.entries(TEAM_ODDS).sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
  const index = sorted.findIndex(([id]) => id === teamId);
  return index >= 0 ? index + 1 : Number.POSITIVE_INFINITY;
}

export function isTopOddsTeam(teamId, limit = 20) {
  return oddsRank(teamId) <= limit;
}

export function isDarkHorseTeam(teamId) {
  return teamOdds(teamId) >= 250;
}

export function favoriteTeams(teams) {
  return [...(teams || [])].sort((a, b) => teamStrength(teams, b.id) - teamStrength(teams, a.id));
}

export function strongestTeam(teams) {
  return [...teams].sort((a, b) => b.strength - a.strength)[0] || teams[0];
}

export function projectedScore(fixture, teams) {
  const home = findTeamById(teams, fixture.homeTeamId);
  const away = findTeamById(teams, fixture.awayTeamId);
  const homeEdge = Number(home?.strength || 140) - Number(away?.strength || 140);
  const homeGoals = Math.max(0, Math.min(4, Math.round(1.35 + homeEdge / 55)));
  const awayGoals = Math.max(0, Math.min(4, Math.round(1.15 - homeEdge / 65)));
  return `${homeGoals}-${awayGoals}`;
}

